import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate } from '@angular/router';
import { TokenService, isTokenExpired, RoleCodes } from '@auth';

@Injectable({ providedIn: 'root' })
export class RouteGuard implements CanActivate {
  constructor(private tokenService: TokenService) {}

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    const allowedRoles = route?.data['roles'] as string[];
    const allowedGroupTypes = route?.data['groupTypes'] as string[];
    const roleCodes = await this.tokenService.getRoleCodes();
    const groupTypeId =
      await this.tokenService.getTokenAttribute('GroupTypeId');

    // If no roles or group types specified, allow access
    if (!allowedRoles && !allowedGroupTypes) {
      return true;
    }

    // Separate positive and negative roles (negative roles start with "-")
    const positiveRoles =
      allowedRoles?.filter((role) => !role.startsWith('-')) || [];
    const negativeRoles =
      allowedRoles
        ?.filter((role) => role.startsWith('-'))
        .map((role) => role.substring(1)) || [];

    // Check negative roles first (user must NOT have any of these)
    if (negativeRoles.length > 0) {
      const hasNegativeRole = negativeRoles.some((role) =>
        roleCodes.includes(role),
      );
      if (hasNegativeRole) {
        return false; // Deny access if user has excluded role
      }
    }

    // If allowedRoles is blank/empty or only has negative roles, skip positive role check (allow)
    const hasRoleCode =
      positiveRoles.length === 0
        ? true
        : positiveRoles.some((role) => roleCodes.includes(role));

    // If allowedGroupTypes is blank/empty, skip group type check (allow)
    const hasGroupType =
      !allowedGroupTypes || allowedGroupTypes.length === 0
        ? true
        : allowedGroupTypes.some((groupType) => groupTypeId === groupType);

    // Both checks must pass if both are specified, or allow if either is blank
    return hasRoleCode && hasGroupType;
  }
}
